import { ChannelsStatusSnapshot } from "../types.ts";
import type { ChannelsState } from "./channels.types.ts";

export type { ChannelsState };

export async function loadChannels(state: ChannelsState, probe: boolean) {
  if (!state.client || !state.connected) {
    return;
  }
  if (state.channelsLoading) {
    return;
  }
  state.channelsLoading = true;
  state.channelsError = null;
  try {
    const res = await state.client.request<ChannelsStatusSnapshot | null>("channels.status", {
      probe,
      timeoutMs: 8000,
    });
    state.channelsSnapshot = res;
    state.channelsLastSuccess = Date.now();
  } catch (err) {
    state.channelsError = String(err);
  } finally {
    state.channelsLoading = false;
  }
}

export async function startWhatsAppLogin(state: ChannelsState, force: boolean) {
  if (!state.client || !state.connected || state.whatsappBusy) {
    return;
  }
  state.whatsappBusy = true;
  try {
    const res = await state.client.request<{ message?: string; qrDataUrl?: string }>(
      "web.login.start",
      {
        force,
        timeoutMs: 30000,
      },
    );
    state.whatsappLoginMessage = res.message ?? null;
    state.whatsappLoginQrDataUrl = res.qrDataUrl ?? null;
    state.whatsappLoginConnected = null;
  } catch (err) {
    state.whatsappLoginMessage = String(err);
    state.whatsappLoginQrDataUrl = null;
    state.whatsappLoginConnected = null;
  } finally {
    state.whatsappBusy = false;
  }
}

export async function waitWhatsAppLogin(state: ChannelsState) {
  if (!state.client || !state.connected || state.whatsappBusy) {
    return;
  }
  state.whatsappBusy = true;
  try {
    const res = await state.client.request<{ message?: string; connected?: boolean }>(
      "web.login.wait",
      {
        timeoutMs: 120000,
      },
    );
    state.whatsappLoginMessage = res.message ?? null;
    state.whatsappLoginConnected = res.connected ?? null;
    if (res.connected) {
      state.whatsappLoginQrDataUrl = null;
    }
  } catch (err) {
    state.whatsappLoginMessage = String(err);
    state.whatsappLoginConnected = null;
  } finally {
    state.whatsappBusy = false;
  }
}

export async function logoutWhatsApp(state: ChannelsState) {
  if (!state.client || !state.connected || state.whatsappBusy) {
    return;
  }
  state.whatsappBusy = true;
  try {
    await state.client.request("channels.logout", { channel: "whatsapp" });
    state.whatsappLoginMessage = "Logged out.";
    state.whatsappLoginQrDataUrl = null;
    state.whatsappLoginConnected = null;
  } catch (err) {
    state.whatsappLoginMessage = String(err);
  } finally {
    state.whatsappBusy = false;
  }
}

export async function startOhzLogin(state: ChannelsState, force: boolean) {
  if (!state.client || !state.connected || state.ohzBusy) {
    return;
  }
  state.ohzBusy = true;
  try {
    const res = await state.client.request<{ message?: string; qrDataUrl?: string }>(
      "web.login.start",
      {
        channel: "onehammerzalo",
        force,
        timeoutMs: 30000,
      },
    );
    state.ohzLoginMessage = res.message ?? null;
    state.ohzLoginQrDataUrl = res.qrDataUrl ?? null;
    state.ohzLoginConnected = null;
  } catch (err) {
    state.ohzLoginMessage = String(err);
    state.ohzLoginQrDataUrl = null;
    state.ohzLoginConnected = null;
  } finally {
    state.ohzBusy = false;
  }
}

export async function waitOhzLogin(state: ChannelsState) {
  if (!state.client || !state.connected || state.ohzBusy) {
    return;
  }
  state.ohzBusy = true;
  try {
    const res = await state.client.request<{ message?: string; connected?: boolean }>(
      "web.login.wait",
      {
        channel: "onehammerzalo",
        timeoutMs: 120000,
      },
    );
    state.ohzLoginMessage = res.message ?? null;
    state.ohzLoginConnected = res.connected ?? null;
    if (res.connected) {
      state.ohzLoginQrDataUrl = null;
    }
  } catch (err) {
    state.ohzLoginMessage = String(err);
    state.ohzLoginConnected = null;
  } finally {
    state.ohzBusy = false;
  }
}

export async function logoutOhz(state: ChannelsState) {
  if (!state.client || !state.connected || state.ohzBusy) {
    return;
  }
  state.ohzBusy = true;
  try {
    await state.client.request("channels.logout", { channel: "onehammerzalo" });
    state.ohzLoginMessage = "Logged out.";
    state.ohzLoginQrDataUrl = null;
    state.ohzLoginConnected = null;
  } catch (err) {
    state.ohzLoginMessage = String(err);
  } finally {
    state.ohzBusy = false;
  }
}

export async function startChannel(state: ChannelsState, channelId: string) {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    await state.client.request("channels.start", { channel: channelId });
    // Refresh channels after starting
    await loadChannels(state, false);
  } catch (err) {
    console.error(`Failed to start channel ${channelId}:`, err);
  }
}

export async function stopChannel(state: ChannelsState, channelId: string) {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    await state.client.request("channels.stop", { channel: channelId });
    // Refresh channels after stopping
    await loadChannels(state, false);
  } catch (err) {
    console.error(`Failed to stop channel ${channelId}:`, err);
  }
}
